/**
 * The What-if panel's "Placed antennas" list: every placed antenna in the
 * dataset with fly-to on its name and Remove (out of the dataset, re-run).
 * Renders nothing until an antenna has been placed.
 */
import { useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { useUI } from '@/state/store';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { getGlobeEngine } from '../globe/engineHandle';
import { useLoader } from '../store';
import { removePlacedAntenna } from './flow';
import { isPlacedAntenna } from './placement';
import { usePlacement } from './store';

export function PlacedAntennasSection() {
  const dataset = useLoader((s) => s.dataset);
  const lastPlacedId = usePlacement((s) => s.lastPlacedId);
  const placed = useMemo(() => (dataset?.antennas ?? []).filter(isPlacedAntenna), [dataset]);
  if (placed.length === 0) return null;

  const remove = (id: string) => {
    try {
      removePlacedAntenna(id);
    } catch (err) {
      console.error(`[place] removing ${id} failed:`, err);
      toast('error', `Could not remove the antenna: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <section aria-label="Placed antennas">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">
        Placed antennas ({placed.length})
      </div>
      <ul className="flex flex-col gap-1">
        {placed.map((a) => (
          <li
            key={a.id}
            className={cn(
              'flex items-center gap-2 rounded-control border px-2 py-1.5',
              a.id === lastPlacedId ? 'border-accent/50' : 'border-hairline',
            )}
          >
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: a.color }} />
            <button
              type="button"
              className="min-w-0 truncate text-left text-ink hover:text-accent"
              title={`${a.name}: select and fly to it`}
              onClick={() => {
                useUI.getState().select({ antennaId: a.id, missionId: null });
                getGlobeEngine()?.flyToAntenna(a.id);
              }}
            >
              {a.shortName}
            </button>
            <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-ink-3">
              {a.capabilities.diameterM} m, {a.location.name}
            </span>
            <button
              type="button"
              aria-label={`Remove ${a.shortName}`}
              onClick={() => remove(a.id)}
              className="flex shrink-0 items-center gap-1 text-ink-3 hover:text-warning"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
